import { useState, useRef, useId, useCallback } from 'react';

export interface UseTextareaProps {
  value?: string | undefined;
  defaultValue?: string | undefined;
  error?: boolean | undefined;
  errorMessage?: string | undefined;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
  id?: string | undefined;
  customLabelColor?: string | undefined;
  onChange?: ((e: React.ChangeEvent<HTMLTextAreaElement>) => void) | undefined;
  onFocus?: ((e: React.FocusEvent<HTMLTextAreaElement>) => void) | undefined;
  onBlur?: ((e: React.FocusEvent<HTMLTextAreaElement>) => void) | undefined;
}

export interface UseTextareaReturn {
  textareaId: string;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
  hasValue: boolean;
  hasError: boolean;
  isFloating: boolean;
  isFocused: boolean;
  labelColor: string;
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleFocus: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  handleContainerClick: () => void;
}

export const useTextarea = ({
  value: controlledValue,
  defaultValue = '',
  error = false,
  errorMessage,
  disabled = false,
  id: providedId,
  customLabelColor,
  onChange,
  onFocus,
  onBlur,
}: UseTextareaProps): UseTextareaReturn => {
  const generatedId = useId();
  const textareaId = providedId || generatedId;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);

  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const hasValue = value.length > 0;
  const hasError = error || !!errorMessage;
  // Label floats only on focus or when there's value (not by placeholder), so the animation is visible
  const isFloating = isFocused || hasValue;

  const getLabelColor = useCallback((): string => {
    if (disabled) {
      return '#b6bcc1';
    }
    if (isFocused) {
      return '#2a4455';
    }
    return customLabelColor || '#6a7278';
  }, [disabled, isFocused, customLabelColor]);

  const labelColor = getLabelColor();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (controlledValue === undefined) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    },
    [controlledValue, onChange]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleContainerClick = useCallback(() => {
    if (!disabled) {
      textareaRef.current?.focus();
    }
  }, [disabled]);

  return {
    textareaId,
    textareaRef,
    value,
    hasValue,
    hasError,
    isFloating,
    isFocused,
    labelColor,
    handleChange,
    handleFocus,
    handleBlur,
    handleContainerClick,
  };
};
